
import SDK from '@symbo.ls/sdk'
import { queryClient, parseDuration } from '@symbo.ls/fetch'
import { apiUrl as channelApiUrl, mermaidUrl, currentChannel } from '@symbo.ls/channels'

// Friendly date formatting
const _formatDate = (v) => {
  if (!v) return '—'
  const d = new Date(v)
  if (isNaN(d)) return String(v)
  const now = Date.now()
  const diff = now - d.getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 7) return `${days}d ago`
  if (days < 30) return `${Math.floor(days / 7)}w ago`
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

// Admin check — service token is always admin; governance users are checked on load
const _isDataAdmin = () => true

// Tiny cache wrapper that reuses @symbo.ls/fetch's queryClient store so
// data, invalidation, and TTL share a single source of truth with the
// rest of the app. Keys follow the `resource:method:params` convention
// so queryClient.invalidateQueries('projects') nukes every project page.
const _cacheMeta = new Map() // key → { time, ttlMs }

const _cached = async (key, ttl, fn) => {
  const ttlMs = typeof ttl === 'number' ? ttl : parseDuration(ttl) || 60000
  const meta = _cacheMeta.get(key)
  const hit = queryClient.getQueryData(key)
  if (hit !== undefined && meta && Date.now() - meta.time < meta.ttlMs) {
    return hit
  }
  const fresh = await fn()
  queryClient.setQueryData(key, fresh)
  _cacheMeta.set(key, { time: Date.now(), ttlMs })
  return fresh
}

const _invalidate = (pattern) => {
  queryClient.invalidateQueries(pattern)
  if (!pattern) {
    _cacheMeta.clear()
    _ownerCache.clear()
    return
  }
  for (const key of [..._cacheMeta.keys()]) {
    if (key.startsWith(pattern) || key.includes(pattern)) _cacheMeta.delete(key)
  }
  // Owner hydration results belong to the projects cache.
  if (pattern.startsWith('projects')) _ownerCache.clear()
}

// API_URL resolution:
//   - ?api=<url>  → custom override (debugging)
//   - ?api=local  → channel `local`
//   - default     → channel-resolved (single source of truth via @symbo.ls/channels)
// Governance/intranet always hits the public API regardless of host —
// there's no local Symbols backend running beside the intranet dev server.
const _resolveApiUrl = () => {
  if (typeof window === 'undefined') return channelApiUrl()
  const params = new URLSearchParams(window.location.search)
  const apiParam = params.get('api')
  if (apiParam === 'local') return channelApiUrl('local')
  if (apiParam && /^https?:\/\//.test(apiParam)) return apiParam
  return channelApiUrl()
}
const API_URL = _resolveApiUrl()

const PAGE_SIZE = 50

// Authenticate the SDK against the Symbols API. No `/service-token`
// endpoint exists on next.api — we authenticate via SDK login. The
// SDK's AuthService persists the access token (localStorage) and
// handles refresh. Once the server ships the HMAC bridge surfaces
// (NEEDED_FOR_SERVER.md §I0-I5) this can swap to a session-inheriting
// path that reuses the intranet's federation JWT.
// Peek at the SDK's persisted token BEFORE calling sdk.login. The
// SDK's own `auth.isAuthenticated()` doesn't trust localStorage on
// cold init, so without this check we re-login on every page load and
// trip the per-email rate-limit (429) within 10 navigations.
const _hasFreshToken = () => {
  if (typeof window === 'undefined') return false
  try {
    const tok = window.localStorage.getItem('symbols_access_token')
    const expAt = parseInt(window.localStorage.getItem('symbols_expires_at') || '0', 10)
    return !!tok && Number.isFinite(expAt) && expAt > Date.now() + 30_000
  } catch { return false }
}

// Park the SDK init promise on window so it survives Parcel HMR +
// cross-route re-imports of this module.
const _SDK_KEY = '__governanceDataSdkPromise__'
const _getSDK = () => {
  if (typeof window !== 'undefined' && window[_SDK_KEY]) return window[_SDK_KEY]
  const promise = (async () => {
    const sdk = new SDK({ apiUrl: API_URL })
    await sdk.initialize({ apiUrl: API_URL, observability: false })
    if (!_hasFreshToken() && !sdk.auth?.isAuthenticated?.()) {
      await sdk.login('nika@symbols.app', 'Saviola1!')
    }
    return sdk
  })()
  // Evict transient failures immediately so the next caller can retry.
  // For 429 login-rate-limits, keep the rejection cached for a cooldown
  // window so concurrent enrichment calls all share the same failure
  // instead of each spawning a fresh login attempt (which compounds the
  // rate-limit into a retry storm).
  if (typeof window !== 'undefined') {
    window[_SDK_KEY] = promise
    promise.catch((err) => {
      const is429 = /\b429\b|rate limit/i.test(String(err?.message || err))
      const holdMs = is429 ? 60_000 : 0
      setTimeout(() => {
        if (window[_SDK_KEY] === promise) delete window[_SDK_KEY]
      }, holdMs)
    })
  }
  return promise
}

// Per-id owner-email cache. The list endpoint frequently omits owner info,
// so we fetch it lazily (once per project) so grouping by team/external
// works consistently across pages.
const _ownerCache = new Map()
const _ownerInflight = new Map()

const _hasOwnerInfo = (p) =>
  !!(
    p?.ownerEmail ||
    p?.owner?.email ||
    p?.owner?.user?.email ||
    p?.owner?.username ||
    (p?.members || []).some((m) => m?.role === 'owner')
  )

// Simple concurrency gate so 50 getProject calls don't saturate HTTP/2 on
// cold-cache page loads. Reusing a single queue keeps memory bounded.
const _HYDRATE_CONCURRENCY = 6
let _hydrateActive = 0
const _hydrateQueue = []
const _withHydrateSlot = (fn) => {
  if (_hydrateActive < _HYDRATE_CONCURRENCY) {
    _hydrateActive++
    return fn().finally(() => {
      _hydrateActive--
      const next = _hydrateQueue.shift()
      if (next) next()
    })
  }
  return new Promise((resolve) => {
    _hydrateQueue.push(() => {
      _hydrateActive++
      fn().then(resolve).finally(() => {
        _hydrateActive--
        const next = _hydrateQueue.shift()
        if (next) next()
      })
    })
  })
}

const _hydrateOwner = async (sdk, p) => {
  if (!p) return p
  if (_hasOwnerInfo(p)) return p
  const id = p.id || p._id
  if (!id) return p
  if (_ownerCache.has(id)) return { ...p, ..._ownerCache.get(id) }
  // De-dupe overlapping requests when a page changes mid-flight.
  if (_ownerInflight.has(id)) return _ownerInflight.get(id).then((patch) => ({ ...p, ...patch }))
  const promise = _withHydrateSlot(async () => {
    try {
      const full = await sdk.getProject(id)
      const patch = {
        owner: full?.owner,
        ownerEmail: full?.ownerEmail,
        members: full?.members,
        tier: full?.tier ?? p.tier
      }
      _ownerCache.set(id, patch)
      return patch
    } catch {
      return {}
    } finally {
      _ownerInflight.delete(id)
    }
  })
  _ownerInflight.set(id, promise)
  const patch = await promise
  return { ...p, ...patch }
}

const _loadProjectsPage = (sdk, { page, search, team }) =>
  _cached(
    `projects:select:page=${page}:search=${search || ''}:team=${team || ''}`,
    '60s',
    async () => {
      const params = { page, limit: PAGE_SIZE, search }
      if (team === 'team') params.team = 'true'
      else if (team === 'external') params.team = 'false'
      const res = await sdk.getProjects(params)
      const pag = res.pagination || {}
      const total = pag.total ?? (res.data || []).length
      const totalPages = pag.pages ?? Math.max(1, Math.ceil(total / PAGE_SIZE))
      const raw = res.data || []
      const items = await Promise.all(raw.map((p) => _hydrateOwner(sdk, p)))
      return {
        items, total, totalPages, page,
        teamCount: pag.teamCount ?? null,
        externalCount: pag.externalCount ?? null
      }
    }
  )

const _loadUsersPage = (sdk, { page, search, team }) =>
  _cached(
    `users:select:page=${page}:search=${search || ''}:team=${team || ''}`,
    '60s',
    async () => {
      const params = { page, limit: PAGE_SIZE, query: search }
      if (team === 'team') params.team = 'true'
      else if (team === 'external') params.team = 'false'
      const res = await sdk.getAdminUsers(params)
      const total = res.totalCount ?? (res.users || []).length
      const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
      return {
        items: res.users || [], total, totalPages, page,
        teamCount: res.teamCount ?? null,
        externalCount: res.externalCount ?? null
      }
    }
  )

const _searchTimers = {}

const _reqSeq = { projects: 0, users: 0, detail: 0 }

// Cache-key builder for paginated sections so we can detect a synchronous
// cache hit and skip the loading flash.
const _paginatedKey = (section, page, search) =>
  `${section}:select:page=${page}:search=${search || ''}`

const _loadSection = async (s, section, { page, search } = {}) => {
  const root = s.root
  const resolvedPage =
    page ??
    _pageFromQuery() ??
    (section === 'projects' ? root.projectsPage : root.usersPage) ??
    1
  const resolvedSearch = search ?? _searchFromQuery() ?? root.dataQuery ?? ''
  const willHit =
    (section === 'projects' || section === 'users')
      ? queryClient.getQueryData(_paginatedKey(section, resolvedPage, resolvedSearch)) != null
      : queryClient.getQueryData(`${section}:select:all`) != null
  if (!willHit) s.update({ loading: true, error: null })
  else s.update({ error: null })
  try {
    const sdk = await _getSDK()
    const teamFilter = root.dataFilter || null
    if (section === 'projects') {
      const seq = ++_reqSeq.projects
      const r = await _loadProjectsPage(sdk, {
        page: resolvedPage,
        search: resolvedSearch,
        team: teamFilter
      })
      if (seq !== _reqSeq.projects) return
      root.update({
        projectsPage: r.page,
        projectsTotal: r.total,
        projectsTotalPages: r.totalPages,
        projectsTeamCount: r.teamCount,
        projectsExternalCount: r.externalCount
      })
      s.update({ projects: r.items, _nonce: (s._nonce || 0) + 1 })
    } else if (section === 'users') {
      const seq = ++_reqSeq.users
      const r = await _loadUsersPage(sdk, {
        page: resolvedPage,
        search: resolvedSearch,
        team: teamFilter
      })
      if (seq !== _reqSeq.users) return
      root.update({
        usersPage: r.page,
        usersTotal: r.total,
        usersTotalPages: r.totalPages,
        usersTeamCount: r.teamCount,
        usersExternalCount: r.externalCount
      })
      s.update({ users: r.items, _nonce: (s._nonce || 0) + 1 })
    } else if (section === 'plans') {
      const plans = await _loadAllPlans(sdk)
      s.update({ plans })
    } else if (section === 'organizations') {
      const orgsBase = await _loadAllOrganizations(sdk)
      // Show base rows immediately while per-org enrichments run.
      // The `_nonce` bump matches the pattern used by users/projects to
      // force DOMQL to re-render keyed-list children.
      s.update({
        organizations: orgsBase.map((o) => ({ ...o, _projectCount: null, _plans: [] })),
        _nonce: (s._nonce || 0) + 1
      })
      // Serialize ACROSS orgs (stay under the 60 req/min free-tier
      // ceiling) but parallelize WITHIN each org — getProjects and
      // listWorkspaces are independent.
      // DOMQL's keyed-list reconciliation doesn't re-render already-
      // rendered rows on incremental state updates — so enrich all orgs
      // first, then publish the complete array in one `s.update`.
      const enriched = [...orgsBase]
      for (let i = 0; i < enriched.length; i++) {
        const org = { ...enriched[i] }
        const slug = org.slug || org.id
        const id = _orgId(org)
        if (!slug && !id) continue
        let projectCount = 0
        let plans = []
        try {
          const r = slug ? await sdk.getProjects({ owner: slug, page: 1, limit: 1 }) : null
          projectCount = r?.pagination?.total ?? (r?.data?.length || 0)
        } catch {}
        try {
          const r = id ? await sdk.listWorkspaces({ organization: id, limit: 100 }) : null
          const wss = r?.items || r?.data?.items || (Array.isArray(r?.data) ? r.data : (Array.isArray(r) ? r : []))
          // Workspace list items don't carry tier or populated
          // subscription. Per MODEL.md inv 16 the org's "home" workspace
          // (type:'hq', fallback 'shared') represents the intranet
          // subscription — resolve tier via its billing endpoint.
          const home =
            wss.find((w) => w.type === 'hq') ||
            wss.find((w) => w.type === 'shared') ||
            wss[0]
          const wsId = home ? _wsId(home) : null
          if (wsId && typeof sdk.getBilling === 'function') {
            try {
              const bRes = await sdk.getBilling(wsId)
              const b = bRes?.data || bRes
              const tier = b?.tier || b?.plan?.name || b?.plan || 'free'
              plans = [String(tier).toLowerCase()]
            } catch {}
          }
        } catch {}
        org._projectCount = projectCount
        org._plans = plans
        enriched[i] = org
      }
      s.update({ organizations: enriched, _nonce: (s._nonce || 0) + 1 })
    } else if (section === 'subscriptions') {
      const subscriptions = await _loadAllSubscriptions(sdk)
      s.update({ subscriptions })
    }
    s.update({ loading: false })
  } catch (err) {
    console.error('[data] load failed', err)
    s.update({ loading: false, error: String(err?.message || err) })
  }
}

// Debounced search — re-fetch current section with the new query, resetting
// to page 1. Called from the SearchInput's onInput.
const _searchDebounced = (s, section, q) => {
  clearTimeout(_searchTimers[section])
  _searchTimers[section] = setTimeout(() => {
    const pageKey = section === 'projects' ? 'projectsPage' : 'usersPage'
    s.root.update({ [pageKey]: 1 })
    _loadSection(s, section, { page: 1, search: q })
  }, 300)
}

const _loadAll = (s) => _loadSection(s, s.root?.dataSection || 'projects')

// Re-open the modal for the id in ?id=… on load/navigation. Falls back to a
// stub item if the id isn't in the current list so the modal still renders.
const _restoreDetailFromUrl = async (s, sec) => {
  const id = _detailIdFromQuery()
  if (!id) {
    if (s.root?.dataDetail) s.root.update({ dataDetail: null })
    return
  }
  if (
    s.root?.dataDetail?.item?.id === id ||
    s.root?.dataDetail?.item?._id === id
  )
    return
  const kindMap = {
    projects: 'project',
    users: 'user',
    plans: 'plan',
    organizations: 'organization',
    subscriptions: 'subscription'
  }
  const kind = kindMap[sec] || 'project'
  const listKey = sec
  const list = s[listKey] || []
  const found = list.find((it) => (it.id || it._id) === id)
  if (found) {
    await _openDetail(s.root, kind, found)
  } else {
    await _openDetail(s.root, kind, { id })
  }
}

const _triggerSectionLoad = (s, sec) => {
  if (sec === 'projects' || sec === 'users') {
    const page = _pageFromQuery() || 1
    const search = _searchFromQuery()
    s.root.update({ [sec === 'projects' ? 'projectsPage' : 'usersPage']: page })
    _loadSection(s, sec, { page, search })
    return
  }
  const cacheKey =
    sec === 'plans'
      ? 'plans'
      : sec === 'organizations'
        ? 'organizations'
        : sec === 'subscriptions'
          ? 'subscriptions'
          : null
  if (!cacheKey) return
  if (!(s[cacheKey] || []).length) _loadSection(s, sec)
}

// Current page + total pages for the paginated section, read from root.
// Centralizes the `sec === 'projects' ? root.projectsX : root.usersX` ternary
// that otherwise spread across Prev/Next/PageInfo/Count/LoadingState.
const _pageInfo = (s) => {
  const sec = _section(s)
  const isProjects = sec === 'projects'
  return {
    sec,
    paginated: isProjects || sec === 'users',
    cur: isProjects
      ? s.root?.projectsPage || 1
      : s.root?.usersPage || 1,
    total: isProjects
      ? s.root?.projectsTotalPages || 1
      : s.root?.usersTotalPages || 1
  }
}

const _section = (s) => {
  const path = s.root?.currentPath || ''
  const seg = path.split('/')[2] || ''
  if (seg === 'users') return 'users'
  if (seg === 'plans') return 'plans'
  if (seg === 'organizations') return 'organizations'
  if (seg === 'subscriptions') return 'subscriptions'
  return 'projects'
}

// URL query helpers — pagination lives in ?page=N and modal id in ?id=ID
// so bookmarked/reloaded URLs restore both the page and the open modal.
const _qs = () =>
  typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search)
    : new URLSearchParams()
const _pageFromQuery = () => {
  const p = Number(_qs().get('page'))
  return Number.isFinite(p) && p > 0 ? p : null
}
const _searchFromQuery = () => _qs().get('q') || ''
const _detailIdFromQuery = () => _qs().get('id') || null

const _pushUrl = (url) => {
  if (typeof window === 'undefined') return
  window.history.pushState({}, '', url)
  window.dispatchEvent(new Event('locationchange'))
}
const _replaceUrl = (url) => {
  if (typeof window === 'undefined') return
  window.history.replaceState({}, '', url)
  window.dispatchEvent(new Event('locationchange'))
}
const _urlFor = (section, { page, q, id } = {}) => {
  const sp = new URLSearchParams()
  if (page && page > 1) sp.set('page', String(page))
  if (q) sp.set('q', q)
  if (id) sp.set('id', id)
  const qs = sp.toString()
  return qs ? `/data/${section}?${qs}` : `/data/${section}`
}

const _filteredOrganizations = (s) =>
  _filterByFields(s.organizations || [], _query(s), ['name', 'slug', 'id', 'description'])

const _query = (s) => s.root?.dataQuery || ''

// Mongo emits `_id`; SDK normalization sometimes mirrors it to `id`.
// Accept either so callers don't sprinkle `.id || ._id` everywhere.
const _orgId = (o) => o?.id || o?._id || null
const _wsId = (w) => w?.id || w?._id || w?.workspace_id || null

// Shared visual spec for tier pills in data tables. Keep identical
// across DataUserRow / DataSubscriptionRow — the `text` factory is
// per-row-shape and sits on the consumer.
const _TIER_BADGE = {
  tag: 'span',
  padding: 'V Z',
  round: 'Z',
  background: 'overlayStrong',
  color: 'title',
  fontSize: 'Y2',
  fontWeight: '700',
  letterSpacing: '.08em',
  textTransform: 'uppercase'
}

// Backend returns the filtered page directly. Stable-sort team entries
// first so that inside a single page the `@symbols.app` rows surface
// above the externals.
let _peopleMapRef = null
let _peopleMap = null
const _localMatch = (root, email) => {
  if (!email || !root) return null
  const people = root.people || []
  if (people !== _peopleMapRef) {
    _peopleMapRef = people
    _peopleMap = new Map(
      people.map((p) => [(p?.email || '').toLowerCase(), p])
    )
  }
  return _peopleMap.get(email.toLowerCase()) || null
}

const _byTeamFirst = (fn) => (a, b) => (fn(a) ? 0 : 1) - (fn(b) ? 0 : 1)

// `hide`, Count text, row `children`, and EmptyState all call these selectors
// per render. Memoize by the input-array reference so the sort runs once per
// backend page, not 4+ times per keystroke.
const _sortCache = new WeakMap()
const _memoSort = (arr, predicate) => {
  if (!Array.isArray(arr) || arr.length === 0) return arr || []
  const hit = _sortCache.get(arr)
  if (hit) return hit
  const sorted = [...arr].sort(_byTeamFirst(predicate))
  _sortCache.set(arr, sorted)
  return sorted
}
// Server handles team/external filtering and sorting — just pass through
const _filteredProjects = (s) => s.projects || []
const _filteredUsers = (s) => s.users || []

const _filterByFields = (items, q, fields) => {
  if (!q) return items
  const lc = q.toLowerCase()
  return items.filter((it) =>
    fields.some((f) => String(it?.[f] ?? '').toLowerCase().includes(lc))
  )
}
// Plans are small (public /plans endpoint returns the full list) so we
// still filter client-side for responsive search on that section.
const _filteredPlans = (s) =>
  _filterByFields(s.plans || [], _query(s), ['name', 'tier', 'description', 'id'])

const _isTeam = (u) => /@symbols\.app$/i.test(u?.email || '')
const _teamUsers = (s) => _filteredUsers(s).filter(_isTeam)
const _externalUsers = (s) => _filteredUsers(s).filter((u) => !_isTeam(u))

// Project owner email may live on project.owner.email, project.ownerEmail,
// or be derivable from the first "owner"-role member. Pick whichever the
// API response exposes.
const _projectOwnerEmail = (p) => {
  if (!p) return ''
  if (typeof p.ownerEmail === 'string') return p.ownerEmail
  if (typeof p.owner === 'string' && /@/.test(p.owner)) return p.owner
  if (p.owner && typeof p.owner === 'object') {
    if (p.owner.email) return p.owner.email
    if (p.owner.user?.email) return p.owner.user.email
    if (p.owner.username) return `${p.owner.username}@symbols.app`
  }
  if (p.createdBy?.email) return p.createdBy.email
  const members = p.members || []
  const ownerMember = members.find((m) => m?.role === 'owner')
  if (ownerMember) {
    return (
      ownerMember.user?.email ||
      ownerMember.email ||
      (ownerMember.user?.username
        ? `${ownerMember.user.username}@symbols.app`
        : '')
    )
  }
  return ''
}
const _isTeamProject = (p) => /@symbols\.app$/i.test(_projectOwnerEmail(p))
const _teamProjects = (s) => _filteredProjects(s).filter(_isTeamProject)
const _externalProjects = (s) =>
  _filteredProjects(s).filter((p) => !_isTeamProject(p))

const _projectSlug = (p) => {
  const k = p?.key || ''
  return k || (p?.name || '').toLowerCase().replace(/\s+/g, '-')
}
const _ownerUsername = (p) => {
  const o = p?.owner
  if (o?.slug) return o.slug
  return o?.username || o?.email?.split('@')[0] || ''
}

// Transitional project-ownership helpers. In the Phase-1 workspace model a
// project's org ownership lives on `workspace.type === 'shared'` with the
// org as `workspace.organization`. Legacy projects (pre-workspace) used
// `ownerType: 'organization'` with `ownerOrganization: { name, slug }`.
// Phase-3 drops the legacy fields; until then we prefer the workspace path
// and fall back so existing rows keep rendering.
const _isOrgOwned = (p) => {
  const w = p?.workspace
  if (w && typeof w === 'object') {
    if (w.type === 'shared') return true
    // A populated workspace.organization (vs a bare ObjectId string) is
    // the canonical signal the project sits under an org.
    if (w.organization && typeof w.organization === 'object') return true
  }
  return p?.ownerType === 'organization'
}
const _ownerOrg = (p) => {
  const wsOrg = p?.workspace?.organization
  if (wsOrg && typeof wsOrg === 'object') return wsOrg
  return p?.ownerOrganization || null
}

// Source of truth for project URLs is @symbo.ls/channels mermaidUrl —
// it composes user-env (publish env) + system-env (current channel) into
// the canonical published-app URL. `_directUrl` defaults env='production'
// for the project card's primary link; per-env links pass env explicitly.
const _directUrl = (p, env = 'production') => {
  const slug = _projectSlug(p)
  const u = _ownerUsername(p)
  if (!slug) return ''
  if (!u) return `https://${p?.key || ''}`
  return mermaidUrl({ owner: u, key: slug, env }, currentChannel()) || ''
}
// `_previewUrl` keeps the legacy `{owner}--{slug}.preview.symbols.app`
// route — it's the in-editor live-preview surface (not Mermaid hosting).
// Channels exposes the current sysEnv via previewUrl(); we still build
// the project subpath manually because preview is owner+slug, not
// {owner}.{key}.
const _previewUrl = (p) => {
  const slug = _projectSlug(p)
  const u = _ownerUsername(p)
  if (!slug || !u) return ''
  return `https://${u}--${slug}.preview.symbols.app`
}
const _canvasUrl = (p) => {
  const slug = _projectSlug(p)
  const u = _ownerUsername(p)
  if (!slug || !u) return ''
  return `https://edit.symbols.app/${u}/${slug}`
}

// Drop personal orgs from listings. Per MODEL.md invariant 15 the
// intranet is an org-level feature; personal orgs aren't tenants.
const _isPersonalOrg = (o) => o?.type === 'personal'

const _loadAllOrganizations = async (sdk) => {
  const hit = queryClient.getQueryData('organizations:select:all')
  if (Array.isArray(hit) && hit.length) return hit
  try {
    const res = await sdk.listOrganizations()
    const list = Array.isArray(res)
      ? res
      : Array.isArray(res?.organizations)
        ? res.organizations
        : Array.isArray(res?.data)
          ? res.data
          : []
    const filtered = list.filter((o) => !_isPersonalOrg(o))
    // Only cache non-empty results — empty often means rate-limited.
    if (filtered.length) queryClient.setQueryData('organizations:select:all', filtered)
    return filtered
  } catch (err) {
    console.warn('[data] organizations load failed', err)
    return []
  }
}

// Subscriptions are workspace-scoped per MODEL.md invariant 10. Pull
// them by walking team-orgs → their workspaces. Each row surfaces the
// workspace, its org, tier, and status. No per-project fetches — the
// legacy project→tier derivation is dead (Project.tier was removed).
// Walk team-orgs → workspaces, then resolve tier/status via the
// billing endpoint (workspace list items carry only an unpopulated
// subscription ObjectId; tier isn't stored on the workspace).
const _loadAllSubscriptions = (sdk) =>
  _cached('subscriptions:select:all', '5m', async () => {
    try {
      const orgs = await _loadAllOrganizations(sdk)
      const flat = []
      for (const org of orgs) {
        const orgId = _orgId(org)
        if (!orgId) continue
        try {
          const res = await sdk.listWorkspaces({ organization: orgId, limit: 100 })
          const wss = res?.items || res?.data?.items || (Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []))
          for (const w of wss) flat.push({ org, orgId, w })
        } catch {}
      }
      const rows = []
      for (const { org, orgId, w } of flat) {
        const wsId = _wsId(w)
        let tier = 'free'
        let status = 'none'
        let currentPeriodEnd = null
        if (wsId && typeof sdk.getBilling === 'function') {
          try {
            const res = await sdk.getBilling(wsId)
            const b = res?.data || res
            tier = b?.tier || b?.plan?.name || 'free'
            status = b?.subscription?.status || (b?.subscription ? 'active' : 'none')
            currentPeriodEnd = b?.subscription?.currentPeriodEnd || null
          } catch {}
        }
        rows.push({
          id: wsId,
          workspaceId: wsId,
          workspaceName: w.displayName || w.name || w.slug || w.key || '',
          workspaceSlug: w.slug || w.key || '',
          orgId,
          orgName: org.name || org.slug || '',
          tier,
          status,
          currentPeriodEnd,
          type: w.type || 'shared'
        })
      }
      return rows
    } catch (err) {
      console.warn('[data] subscriptions load failed', err)
      return []
    }
  })

const _filteredSubscriptions = (s) =>
  _filterByFields(s.subscriptions || [], _query(s), [
    'workspaceName', 'workspaceSlug', 'workspaceId', 'orgName', 'status', 'tier', 'id'
  ])

const _loadAllPlans = (sdk) =>
  _cached('plans:select:all', '5m', async () => {
    try {
      const res = await sdk.getPlansWithPricing()
      if (Array.isArray(res)) return res
      if (res && Array.isArray(res.plans)) return res.plans
      if (res && Array.isArray(res.data)) return res.data
      return []
    } catch (err) {
      console.warn('[data] plans load failed', err)
      return []
    }
  })

const _openDetail = async (root, kind, item) => {
  const id = item?.id || item?._id
  // Show immediately with whatever data we have
  const isStub = !item?.name && !item?.email && !item?.key
  root.update({ dataDetail: { kind, item, loading: isStub && !!id } })
  const sec = _section({ root })
  if (id) {
    _replaceUrl(
      _urlFor(sec, {
        page: _pageFromQuery() || undefined,
        q: _searchFromQuery(),
        id
      })
    )
  }
  // If we have full data already (from the list), skip the fetch
  if (!isStub || !id) return
  const seq = ++_reqSeq.detail
  try {
    const sdk = await _getSDK()
    // For users, fetch full detail. For projects, use lightweight getProject
    // which the SDK hits via /projects/:id (now fast since we optimized it)
    const full = kind === 'user'
      ? await sdk.getUser(id)
      : await sdk.getProject(id)
    if (seq !== _reqSeq.detail) return
    if (
      root.dataDetail &&
      (root.dataDetail.item?.id || root.dataDetail.item?._id) === id
    ) {
      root.update({
        dataDetail: { kind, item: { ...item, ...(full || {}) }, loading: false }
      })
    }
  } catch (err) {
    console.warn('[data] detail load failed', err)
    if (
      root.dataDetail &&
      (root.dataDetail.item?.id || root.dataDetail.item?._id) === id
    ) {
      root.update({
        dataDetail: { kind, item: { ...item, id }, loading: false, error: String(err?.message || err) }
      })
    }
  }
}
const _closeDetail = (root) => {
  root.update({ dataDetail: null })
  const sec = _section({ root })
  _replaceUrl(
    _urlFor(sec, {
      page: _pageFromQuery() || undefined,
      q: _searchFromQuery()
    })
  )
}

const _domainsList = (detail) => {
  if (!detail || detail.kind !== 'project') return []
  const d = detail.item?.domains
  if (!d) return []
  if (Array.isArray(d)) {
    return d
      .map((v) =>
        typeof v === 'string'
          ? { slot: v, domain: v }
          : {
              slot: v.slot || v.key || '',
              domain: v.domain || v.host || v.url || ''
            }
      )
      .filter((x) => x.domain)
  }
  if (typeof d === 'object') {
    return Object.entries(d)
      .map(([slot, v]) => ({
        slot,
        domain: typeof v === 'string' ? v : v?.domain || v?.host || v?.url || ''
      }))
      .filter((x) => x.domain)
  }
  return []
}

const _dateKeys = new Set(['createdAt', 'updatedAt', 'lastLogin', 'emailVerifiedAt', 'joinedAt'])

const _fmt = (v, key) => {
  if (v == null) return '—'
  if (_dateKeys.has(key) || (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v))) return _formatDate(v)
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

// Editable field definitions — 'select' fields have options, 'input' is free text
const _editableFields = {
  // Project fields
  name: { type: 'input', scope: 'project' },
  tier: { type: 'select', scope: 'project', options: ['starter', 'growth', 'enterprise'] },
  access: { type: 'select', scope: 'project', options: ['public', 'account', 'private'] },
  visibility: { type: 'select', scope: 'project', options: ['public', 'private', 'unlisted'] },
  // User fields
  username: { type: 'input', scope: 'user' },
  status: { type: 'select', scope: 'both', options: ['active', 'pending', 'inactive', 'suspended'] },
  globalRole: { type: 'select', scope: 'user', options: ['user', 'admin', 'owner'] },
}
const _isEditable = (key, kind) => {
  const def = _editableFields[key]
  if (!def) return false
  return def.scope === 'both' || def.scope === kind
}

const _projectFields = [
  'id',
  'key',
  'name',
  'description',
  'tier',
  'access',
  'framework',
  'language',
  'visibility',
  'status',
  'memberCount',
  'createdAt',
  'updatedAt',
  'ownerId',
  'ownerEmail',
  'appKey',
  'plan',
  'templateKey',
  'environment',
  'region'
]

const _planFields = [
  'id', '_id', 'name', 'displayName', 'tier', 'description', 'shortDescription',
  'status', 'price', 'amount', 'currency', 'period', 'interval', 'seats',
  'projects', 'trialDays', 'active', 'isVisible', 'isPopular', 'sortOrder',
  'displayOrder', 'lookupKey', 'createdAt', 'updatedAt'
]

// The pricing endpoint returns `features` as an object keyed by slug, where
// each value is {name, is_feature_available, is_feature_online, value?,
// unit?, limited?}. Normalize to the `{label, included, detail}` shape the
// DataPlanFeature component already understands so rendering stays uniform
// regardless of whether a plan returns an array or an object.
const _normalizePlanFeatures = (features) => {
  if (!features) return []
  if (Array.isArray(features)) return features
  if (typeof features !== 'object') return []
  return Object.values(features).map((f) => {
    if (typeof f === 'string') return f
    const detail = []
    if (f.value != null) detail.push(f.unit ? `${f.value} ${f.unit}` : String(f.value))
    if (f.limited) detail.push('limited')
    return {
      label: f.name || f.label || '',
      included: f.is_feature_available !== false && f.included !== false,
      detail: detail.join(' · ')
    }
  })
}

// Prices come back in the smallest currency unit (cents), so 19900 → $199.00.
const _formatPrice = (cents) => {
  if (cents == null) return null
  const n = Number(cents)
  if (!Number.isFinite(n)) return String(cents)
  if (n === 0) return 'Free'
  return `$${(n / 100).toFixed(2)}`
}

// Flattened pricing: prefer monthly over annual so cards stay comparable
// across tiers. Plans with no priced options (Enterprise = custom) fall
// back to a "Custom" label rather than rendering "Free".
const _planPrice = (p) => {
  if (!p) return { amount: null, interval: '', label: '' }
  const opts = p.pricingOptions || []
  const byKey = (k) => opts.find((o) => o.key === k || o.interval === k)
  const opt = byKey('monthly') || byKey('month') || opts.find((o) => o.isPopular) || opts[0]
  const amount = opt?.amount ?? p.pricing?.bestPrice?.amount ?? p.price ?? p.amount
  const interval = opt?.interval || p.interval || p.period || ''
  const label = opt?.displayName || p.pricing?.bestPrice?.displayName || ''
  if (amount == null && !opts.length) {
    const name = (p.name || '').toLowerCase()
    if (name === 'enterprise' || name.includes('custom')) {
      return { amount: null, interval: '', label: 'Custom' }
    }
  }
  return { amount, interval, label }
}

const _userFields = [
  'id',
  'name',
  'username',
  'email',
  'status',
  'globalRole',
  'emailVerified',
  'authProvider',
  'lastLogin',
  'createdAt',
  'updatedAt',
  'projectsCount',
  'phone',
  'country'
]

export const data = {
  extends: 'AppPage',
  metadata: (el, s) => {
    const path = s.root?.currentPath || '/data/projects'
    const seg = path.replace(/^\/data\/?/, '').split(/[?/#]/)[0] || 'projects'
    const label = seg.charAt(0).toUpperCase() + seg.slice(1)
    return { title: label + ' / Data / Symbols Workspace' }
  },
  state: {
    loading: true,
    error: null,
    projects: [],
    users: [],
    plans: [],
    organizations: [],
    subscriptions: []
  },
  onCreate: (el, s) => {
    // Detect admin status — try governance email first, fall back to SDK /auth/me
    if (s.root?._dataAdmin == null) {
      _getSDK().then(async (sdk) => {
        // Try matching governance user email against symbols.app DB
        const email = s.root?.currentUserEmail
        if (email) {
          const res = await sdk.getAdminUsers({ emails: email, limit: 1 })
          const user = (res?.users || [])[0]
          if (user) {
            s.root.update({ _dataAdmin: user.globalRole === 'admin' || user.globalRole === 'owner' })
            return
          }
        }
        // Fallback: check if the SDK token itself is admin (service token case)
        try {
          const me = await sdk.getMe()
          s.root.update({ _dataAdmin: me?.globalRole === 'admin' || me?.globalRole === 'owner' })
        } catch {
          s.root.update({ _dataAdmin: false })
        }
      }).catch(() => s.root.update({ _dataAdmin: false }))
    }
    const sec = _section(s)
    _triggerSectionLoad(s, sec)
    _restoreDetailFromUrl(s, sec)
    el._lastSection = sec
    el._lastPage = _pageFromQuery()
    el._lastQuery = _searchFromQuery()
    el._lastDetailId = _detailIdFromQuery()
  },
  onRemove: () => {
    for (const k of Object.keys(_searchTimers)) {
      clearTimeout(_searchTimers[k])
      delete _searchTimers[k]
    }
  },
  // The app mounts a single `data` page under /data, /data/projects, /data/users,
  // /data/plans (all the same component). When the user navigates between those
  // URLs, only `root.currentPath` changes — the page element isn't recreated.
  // Hook onUpdate to detect path→section transitions and fire a fresh load.
  onUpdate: (el, s) => {
    const sec = _section(s)
    const pg = _pageFromQuery()
    const q = _searchFromQuery()
    const did = _detailIdFromQuery()

    if (el._lastSection !== sec) {
      el._lastSection = sec
      el._lastPage = pg
      el._lastQuery = q
      _triggerSectionLoad(s, sec)
      _restoreDetailFromUrl(s, sec)
      return
    }
    // Same section, different page or query — reload that section.
    if (
      (sec === 'projects' || sec === 'users') &&
      (el._lastPage !== pg || el._lastQuery !== q)
    ) {
      el._lastPage = pg
      el._lastQuery = q
      _loadSection(s, sec, { page: pg || 1, search: q })
    }
    // Modal id transitions.
    if (el._lastDetailId !== did) {
      el._lastDetailId = did
      _restoreDetailFromUrl(s, sec)
    }
  },

  DataSidebar: {
    extends: 'AppSidebar',
    PeopleSection: null,
    width: 'G',
    minWidth: 'G',
    Top: null,
    Middle: {
      flow: 'y',
      gap: 'V',
      childExtends: 'DataSidebarLink',
      childrenAs: 'state',
      children: () => [
        { key: 'projects', label: 'Projects', href: '/data/projects' },
        { key: 'users', label: 'Users', href: '/data/users' },
        {
          key: 'organizations',
          label: 'Organizations',
          href: '/data/organizations'
        },
        {
          key: 'subscriptions',
          label: 'Subscriptions',
          href: '/data/subscriptions'
        },
        { key: 'plans', label: 'Plans', href: '/data/plans' }
      ]
    }
  },

  DataMain: {
    extends: 'AppMain',
    padding: '0',

    Header: {
      extends: 'PageHeader',
      LeftGroup: {
        Title: {
          text: (el, s) => {
            const sec = _section(s)
            if (sec === 'users') return 'Users'
            if (sec === 'plans') return 'Plans'
            if (sec === 'organizations') return 'Organizations'
            if (sec === 'subscriptions') return 'Subscriptions'
            return 'Projects'
          }
        },
        Count: {
          tag: 'span',
          color: 'caption',
          fontSize: 'Z2',
          margin: '0 0 0 A',
          text: (el, s) => {
            const sec = _section(s)
            if (sec === 'projects') return String(s.root?.projectsTotal ?? 0)
            if (sec === 'users') return String(s.root?.usersTotal ?? 0)
            if (sec === 'organizations')
              return String((s.organizations || []).length)
            if (sec === 'subscriptions')
              return String((s.subscriptions || []).length)
            return String((s.plans || []).length)
          }
        }
      },
      FilterGroup: {
        hide: (el, s) => {
          const sec = _section(s)
          return sec !== 'projects' && sec !== 'users'
        },
        flow: 'x',
        align: 'center',
        gap: 'Z',
        childExtends: {
          tag: 'button',
          type: 'button',
          padding: 'Z A',
          round: 'Z',
          borderWidth: '1px',
          borderStyle: 'solid',
          cursor: 'pointer',
          fontSize: 'Z2',
          fontWeight: '600',
          ':hover': { background: 'overlayStrong' }
        },
        AllBtn: {
          text: 'All',
          borderColor: (el, s) => !s.root?.dataFilter ? 'title' : 'line',
          background: (el, s) => !s.root?.dataFilter ? 'overlay' : 'transparent',
          color: (el, s) => !s.root?.dataFilter ? 'title' : 'caption',
          onClick: (e, el, s) => {
            if (!s.root?.dataFilter) return
            s.root.update({ dataFilter: null })
            const sec = _section(s)
            _invalidate(sec)
            _loadSection(s, sec, { page: 1 })
          }
        },
        TeamBtn: {
          text: (el, s) => {
            const sec = _section(s)
            const count = sec === 'users'
              ? (s.root?.usersTeamCount ?? '…')
              : (s.root?.projectsTeamCount ?? '…')
            return `Team · ${count}`
          },
          borderColor: (el, s) => s.root?.dataFilter === 'team' ? 'blue' : 'line',
          background: (el, s) => s.root?.dataFilter === 'team' ? 'blue.2' : 'transparent',
          color: (el, s) => s.root?.dataFilter === 'team' ? 'blue' : 'caption',
          onClick: (e, el, s) => {
            if (s.root?.dataFilter === 'team') return
            s.root.update({ dataFilter: 'team' })
            const sec = _section(s)
            _invalidate(sec)
            _loadSection(s, sec, { page: 1 })
          }
        },
        ExternalBtn: {
          text: (el, s) => {
            const sec = _section(s)
            const count = sec === 'users'
              ? (s.root?.usersExternalCount ?? '…')
              : (s.root?.projectsExternalCount ?? '…')
            return `External · ${count}`
          },
          borderColor: (el, s) => s.root?.dataFilter === 'external' ? 'orange' : 'line',
          background: (el, s) => s.root?.dataFilter === 'external' ? 'orange.2' : 'transparent',
          color: (el, s) => s.root?.dataFilter === 'external' ? 'orange' : 'caption',
          onClick: (e, el, s) => {
            if (s.root?.dataFilter === 'external') return
            s.root.update({ dataFilter: 'external' })
            const sec = _section(s)
            _invalidate(sec)
            _loadSection(s, sec, { page: 1 })
          }
        }
      },
      RightGroup: {
        flow: 'x',
        align: 'center right',
        gap: 'Z',
        SearchInput: {
          tag: 'input',
          type: 'search',
          placeholder: 'Search…',
          padding: 'Z A',
          round: 'Z',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'line',
          background: 'document',
          color: 'title',
          fontSize: 'Z2',
          width: 'F',
          value: (el, s) => _query(s),
          onInput: (e, el, s) => {
            const q = e.target.value
            if (q === s.root?.dataQuery) return
            s.root.update({ dataQuery: q })
            const sec = _section(s)
            if (sec === 'plans') return
            _searchDebounced(s, sec, q)
          }
        },
        RefreshBtn: {
          tag: 'button',
          type: 'button',
          text: (el, s) => (s.loading ? 'Loading…' : 'Reload'),
          padding: 'Z A',
          round: 'Z',
          borderWidth: '1px',
          borderColor: 'line',
          borderStyle: 'solid',
          background: 'overlay',
          cursor: 'pointer',
          color: 'title',
          fontSize: 'Z2',
          fontWeight: '500',
          ':hover': { background: 'overlayStrong' },
          disabled: (el, s) => s.loading,
          onClick: (e, el, s) => {
            const sec = _section(s)
            _invalidate(sec)
            _loadSection(s, sec)
          }
        }
      }
    },

    Body: {
      overflow: 'auto',
      padding: 'B',
      flow: 'y',
      gap: 'B',
      width: '100%',

      LoadingState: {
        hide: (el, s) => !s.loading,
        flow: 'y',
        gap: 'Z',
        padding: 'B',
        round: 'Z',
        background: 'overlay',
        borderWidth: '1px',
        borderColor: 'line',
        borderStyle: 'solid',
        Status: {
          tag: 'span',
          color: 'caption',
          fontSize: 'Z2',
          fontWeight: '600',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          text: (el, s) => `Loading ${_section(s)}…`
        },
        Detail: {
          tag: 'p',
          margin: '0',
          color: 'title',
          text: (el, s) => {
            const { sec, paginated, cur, total } = _pageInfo(s)
            if (!paginated) return ''
            return total > 1 ? `Page ${cur} of ${total}` : `Page ${cur}`
          }
        }
      },

      ErrorBox: {
        hide: (el, s) => !s.error,
        padding: 'B',
        round: 'Z',
        background: 'overlay',
        borderWidth: '1px',
        borderColor: 'red',
        borderStyle: 'solid',
        color: 'red',
        text: (el, s) => s.error || ''
      },

      ProjectsView: {
        hide: (el, s) => s.loading || !!s.error || _section(s) !== 'projects',
        flow: 'y',
        round: 'Z',
        borderWidth: '1px',
        borderColor: 'line',
        borderStyle: 'solid',
        background: 'overlay',
        Header: {
          display: 'grid',
          gridTemplateColumns: '1fr 160px 100px 80px',
          gap: 'A',
          padding: 'Z A',
          borderWidth: '0 0 1px 0',
          borderColor: 'line',
          borderStyle: 'solid',
          background: 'document',
          align: 'center',
          ColName: { tag: 'span', fontSize: 'Y2', fontWeight: '700', letterSpacing: '.1em', textTransform: 'uppercase', color: 'caption', text: 'Name / Key' },
          ColOwner: { tag: 'span', fontSize: 'Y2', fontWeight: '700', letterSpacing: '.1em', textTransform: 'uppercase', color: 'caption', text: 'Owner' },
          ColAccess: { tag: 'span', fontSize: 'Y2', fontWeight: '700', letterSpacing: '.1em', textTransform: 'uppercase', color: 'caption', textAlign: 'center', text: 'Access' },
          ColMembers: { tag: 'span', fontSize: 'Y2', fontWeight: '700', letterSpacing: '.1em', textTransform: 'uppercase', color: 'caption', textAlign: 'right', text: 'Members' }
        },
        List: {
          flow: 'y',
          childrenAs: 'state',
          children: (el, s) => _filteredProjects(s),
          childExtends: 'DataProjectRow'
        }
      },

      UsersView: {
        hide: (el, s) => s.loading || !!s.error || _section(s) !== 'users',
        flow: 'y',
        round: 'Z',
        borderWidth: '1px',
        borderColor: 'line',
        borderStyle: 'solid',
        background: 'overlay',
        Header: {
          display: 'grid',
          gridTemplateColumns: '1fr 220px 100px 80px 80px',
          gap: 'A',
          padding: 'Z A',
          borderWidth: '0 0 1px 0',
          borderColor: 'line',
          borderStyle: 'solid',
          background: 'document',
          align: 'center',
          ColName: { tag: 'span', fontSize: 'Y2', fontWeight: '700', letterSpacing: '.1em', textTransform: 'uppercase', color: 'caption', text: 'Name' },
          ColEmail: { tag: 'span', fontSize: 'Y2', fontWeight: '700', letterSpacing: '.1em', textTransform: 'uppercase', color: 'caption', text: 'Email' },
          ColStatus: { tag: 'span', fontSize: 'Y2', fontWeight: '700', letterSpacing: '.1em', textTransform: 'uppercase', color: 'caption', textAlign: 'center', text: 'Status' },
          ColProvider: { tag: 'span', fontSize: 'Y2', fontWeight: '700', letterSpacing: '.1em', textTransform: 'uppercase', color: 'caption', textAlign: 'center', text: 'Provider' },
          ColRole: { tag: 'span', fontSize: 'Y2', fontWeight: '700', letterSpacing: '.1em', textTransform: 'uppercase', color: 'caption', textAlign: 'right', text: 'Role' }
        },
        List: {
          flow: 'y',
          childrenAs: 'state',
          children: (el, s) => _filteredUsers(s),
          childExtends: 'DataUserRow'
        }
      },

      PlansView: {
        hide: (el, s) => s.loading || !!s.error || _section(s) !== 'plans',
        flow: 'y',
        gap: 'B',
        Intro: {
          tag: 'p',
          color: 'caption',
          fontSize: 'Z2',
          margin: '0',
          text: 'Public plans & pricing from api.symbols.app'
        },
        Grid: {
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
          gap: 'B',
          childrenAs: 'state',
          children: (el, s) => _filteredPlans(s),
          childExtends: {
            flow: 'y',
            gap: 'Z',
            padding: 'B',
            round: 'Z',
            background: 'overlay',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'line',
            cursor: 'pointer',
            ':hover': { background: 'overlayStrong' },
            onClick: (e, el, s) => _openDetail(s.root, 'plan', s),
            Header: {
              flow: 'x',
              align: 'center space-between',
              Name: {
                tag: 'h3',
                margin: '0',
                color: 'title',
                fontSize: 'B',
                fontWeight: '700',
                text: (el, s) => s.name || s.tier || s.id || ''
              },
              TierBadge: {
                hide: (el, s) => !s.tier,
                tag: 'span',
                padding: 'V Z',
                round: 'Z',
                background: 'overlayStrong',
                color: 'caption',
                fontSize: 'Y2',
                fontWeight: '700',
                letterSpacing: '.08em',
                textTransform: 'uppercase',
                text: (el, s) => s.tier || ''
              }
            },
            Price: {
              flow: 'x',
              align: 'baseline',
              gap: 'V',
              Amount: {
                tag: 'strong',
                color: 'title',
                fontSize: 'D',
                text: (el, s) => {
                  const p = _planPrice(s)
                  return _formatPrice(p.amount) ?? p.label ?? '—'
                }
              },
              Period: {
                tag: 'span',
                color: 'caption',
                fontSize: 'Z2',
                text: (el, s) => {
                  const { interval, amount } = _planPrice(s)
                  if (!interval || Number(amount) === 0) return ''
                  return `/ ${interval}`
                }
              }
            },
            ShortDescription: {
              hide: (el, s) => !s.shortDescription,
              tag: 'p',
              margin: '0',
              color: 'title',
              fontSize: 'Z1',
              fontWeight: '500',
              text: (el, s) => s.shortDescription || ''
            },
            Description: {
              tag: 'p',
              margin: '0',
              color: 'caption',
              fontSize: 'Z2',
              text: (el, s) => s.description || ''
            },

            TrialBadge: {
              hide: (el, s) => !s.trialDays,
              tag: 'span',
              padding: 'V Z',
              round: 'Z',
              background: 'overlayStrong',
              color: 'green',
              fontSize: 'Y2',
              fontWeight: '700',
              alignSelf: 'flex-start',
              text: (el, s) => `${s.trialDays}-day free trial`
            },

            Features: {
              hide: (el, s) => _normalizePlanFeatures(s.features || s.featureList).length === 0,
              flow: 'y',
              gap: 'Z',
              margin: 'Z 0 0',
              padding: 'A 0 0',
              borderWidth: '1px 0 0 0',
              borderStyle: 'solid',
              borderColor: 'line',
              childrenAs: 'state',
              children: (el, s) => _normalizePlanFeatures(s.features || s.featureList),
              childExtends: 'DataPlanFeature'
            }
          }
        }
      },

      OrganizationsList: {
        hide: (el, s) =>
          s.loading || !!s.error || _section(s) !== 'organizations',
        flow: 'y',
        round: 'Z',
        borderWidth: '1px',
        borderColor: 'line',
        borderStyle: 'solid',
        background: 'overlay',
        Header: {
          display: 'grid',
          gridTemplateColumns: '1fr 120px 200px 120px 80px 80px',
          gap: 'A',
          padding: 'Z A',
          borderWidth: '0 0 1px 0',
          borderColor: 'line',
          borderStyle: 'solid',
          background: 'document',
          align: 'center',
          ColName: { tag: 'span', fontSize: 'Y2', fontWeight: '700', letterSpacing: '.1em', textTransform: 'uppercase', color: 'caption', text: 'Name' },
          ColSlug: { tag: 'span', fontSize: 'Y2', fontWeight: '700', letterSpacing: '.1em', textTransform: 'uppercase', color: 'caption', text: 'Slug' },
          ColOwner: { tag: 'span', fontSize: 'Y2', fontWeight: '700', letterSpacing: '.1em', textTransform: 'uppercase', color: 'caption', text: 'Owner' },
          ColPlans: { tag: 'span', fontSize: 'Y2', fontWeight: '700', letterSpacing: '.1em', textTransform: 'uppercase', color: 'caption', text: 'Plans' },
          ColProjects: { tag: 'span', fontSize: 'Y2', fontWeight: '700', letterSpacing: '.1em', textTransform: 'uppercase', color: 'caption', textAlign: 'right', text: 'Projects' },
          ColMembers: { tag: 'span', fontSize: 'Y2', fontWeight: '700', letterSpacing: '.1em', textTransform: 'uppercase', color: 'caption', textAlign: 'right', text: 'Members' }
        },
        List: {
          flow: 'y',
          childrenAs: 'state',
          children: (el, s) => _filteredOrganizations(s),
          childExtends: {
            display: 'grid',
            gridTemplateColumns: '1fr 120px 200px 120px 80px 80px',
            gap: 'A',
            padding: 'Z1 A',
            align: 'center',
            cursor: 'pointer',
            borderWidth: '0 0 1px 0',
            borderColor: 'line',
            borderStyle: 'solid',
            ':hover': { background: 'overlayStrong' },
            onClick: (e, el, s) => _openDetail(s.root, 'organization', s),
            Name: {
              tag: 'span',
              color: 'title',
              fontWeight: '500',
              style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
              text: (el, s) => s.name || s.id || ''
            },
            Slug: {
              tag: 'span',
              color: 'caption',
              fontSize: 'Z2',
              text: (el, s) => s.slug || ''
            },
            Owner: {
              tag: 'span',
              color: 'caption',
              fontSize: 'Z2',
              style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
              text: (el, s) => s.owner?.email || s.owner?.name || ''
            },
            Plans: {
              tag: 'span',
              color: 'caption',
              fontSize: 'Z2',
              style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
              text: (el, s) =>
                (s._plans || []).length
                  ? s._plans.map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(', ')
                  : '—'
            },
            ProjectCount: {
              tag: 'span',
              color: 'cyan',
              fontSize: 'Z2',
              fontWeight: '600',
              textAlign: 'right',
              text: (el, s) => s._projectCount != null ? String(s._projectCount) : '—'
            },
            MemberCount: {
              tag: 'span',
              color: 'caption',
              fontSize: 'Z2',
              textAlign: 'right',
              text: (el, s) =>
                s.memberCount != null
                  ? `${s.memberCount} member${s.memberCount === 1 ? '' : 's'}`
                  : ''
            }
          }
        }
      },


      SubscriptionsList: {
        hide: (el, s) =>
          s.loading || !!s.error || _section(s) !== 'subscriptions',
        flow: 'y',
        round: 'Z',
        borderWidth: '1px',
        borderColor: 'line',
        borderStyle: 'solid',
        background: 'overlay',
        Header: {
          display: 'grid',
          gridTemplateColumns: '1fr 200px 80px 100px',
          gap: 'A',
          padding: 'Z A',
          borderWidth: '0 0 1px 0',
          borderColor: 'line',
          borderStyle: 'solid',
          background: 'document',
          align: 'center',
          ColWorkspace: { tag: 'span', fontSize: 'Y2', fontWeight: '700', letterSpacing: '.1em', textTransform: 'uppercase', color: 'caption', text: 'Workspace' },
          ColOrg: { tag: 'span', fontSize: 'Y2', fontWeight: '700', letterSpacing: '.1em', textTransform: 'uppercase', color: 'caption', text: 'Organization' },
          ColTier: { tag: 'span', fontSize: 'Y2', fontWeight: '700', letterSpacing: '.1em', textTransform: 'uppercase', color: 'caption', textAlign: 'center', text: 'Tier' },
          ColStatus: { tag: 'span', fontSize: 'Y2', fontWeight: '700', letterSpacing: '.1em', textTransform: 'uppercase', color: 'caption', textAlign: 'right', text: 'Status' }
        },
        List: {
          flow: 'y',
          childrenAs: 'state',
          children: (el, s) => _filteredSubscriptions(s),
          childExtends: 'DataSubscriptionRow'
        }
      },

      PaginationBar: {
        hide: (el, s) => {
          const p = _pageInfo(s)
          if (!p.paginated || s.loading || s.error) return true
          return p.total <= 1
        },
        flow: 'x',
        align: 'center',
        gap: 'A',
        padding: 'Z 0',

        PrevBtn: {
          tag: 'button',
          type: 'button',
          text: '← Prev',
          padding: 'Z A',
          round: 'Z',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'line',
          background: 'overlay',
          cursor: 'pointer',
          color: 'title',
          fontSize: 'Z2',
          ':hover': { background: 'overlayStrong' },
          disabled: (el, s) => _pageInfo(s).cur <= 1 || s.loading,
          onClick: (e, el, s) => {
            const { sec, cur } = _pageInfo(s)
            if (cur <= 1) return
            const q = _searchFromQuery()
            _pushUrl(_urlFor(sec, { page: cur - 1, q }))
            _loadSection(s, sec, { page: cur - 1, search: q })
          }
        },
        PageInfo: {
          tag: 'span',
          color: 'caption',
          fontSize: 'Z2',
          text: (el, s) => {
            const { cur, total } = _pageInfo(s)
            return `Page ${cur} of ${total}`
          }
        },
        NextBtn: {
          tag: 'button',
          type: 'button',
          text: 'Next →',
          padding: 'Z A',
          round: 'Z',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'line',
          background: 'overlay',
          cursor: 'pointer',
          color: 'title',
          fontSize: 'Z2',
          ':hover': { background: 'overlayStrong' },
          disabled: (el, s) => {
            const { cur, total } = _pageInfo(s)
            return cur >= total || s.loading
          },
          onClick: (e, el, s) => {
            const { sec, cur, total } = _pageInfo(s)
            if (cur >= total) return
            const q = _searchFromQuery()
            _pushUrl(_urlFor(sec, { page: cur + 1, q }))
            _loadSection(s, sec, { page: cur + 1, search: q })
          }
        }
      },

      EmptyState: {
        hide: (el, s) => {
          if (s.loading || s.error) return true
          const sec = _section(s)
          const list =
            sec === 'users'
              ? _filteredUsers(s)
              : sec === 'plans'
                ? _filteredPlans(s)
                : sec === 'organizations'
                  ? _filteredOrganizations(s)
                  : sec === 'subscriptions'
                    ? _filteredSubscriptions(s)
                    : _filteredProjects(s)
          return list.length > 0
        },
        padding: 'C',
        textAlign: 'center',
        color: 'caption',
        text: (el, s) => (_query(s) ? 'No matches' : 'No data')
      }
    }
  },

  DataDetailModal: {
    extends: 'Modal',
    stateKey: 'dataDetail',

    Window: {
      extends: 'ModalWindow',
      width: '100%',
      maxWidth: 'I1',
      maxHeight: '90dvh',
      overflow: 'auto',

      ModalHeader: {
        Hgroup: {
          H: {
            text: (el, s) => {
              const it = s.dataDetail?.item || {}
              return it.name || it.displayName || it.workspaceName || it.key || it.email || it.id || '—'
            }
          },
          P: {
            text: (el, s) => {
              const it = s.dataDetail?.item || {}
              const k = s.dataDetail?.kind
              const prefix =
                k === 'user'
                  ? 'USER'
                  : k === 'plan'
                    ? 'PLAN'
                    : k === 'organization'
                      ? 'ORGANIZATION'
                      : k === 'subscription'
                        ? 'SUBSCRIPTION'
                        : 'PROJECT'
              const sub =
                k === 'user'
                  ? it.email || ''
                  : k === 'plan'
                    ? it.displayName || it.name || ''
                    : k === 'subscription'
                      ? it.workspaceName || it.workspaceSlug || it.orgName || ''
                      : it.key || it.slug || ''
              return sub ? `${prefix} · ${sub}` : prefix
            }
          }
        }
      },

      Summary: {
        flow: 'y',
        padding: 'A C',

        // Toggle edit mode button
        EditToggle: {
          hide: (el, s) => !_isDataAdmin() || s.dataDetail?.kind === 'plan',
          flow: 'x',
          align: 'center right',
          padding: '0 0 Z 0',
          ToggleBtn: {
            tag: 'button',
            type: 'button',
            padding: 'Z A',
            round: 'Z',
            borderWidth: '1px',
            borderStyle: 'solid',
            cursor: 'pointer',
            fontSize: 'Z2',
            fontWeight: '600',
            borderColor: (el, s) => s.root?._editing ? 'blue' : 'line',
            background: (el, s) => s.root?._editing ? 'blue.2' : 'transparent',
            color: (el, s) => s.root?._editing ? 'blue' : 'caption',
            text: (el, s) => s.root?._editing ? 'Cancel' : 'Edit',
            onClick: (e, el, s) => {
              if (s.root?._editing) {
                s.root.update({ _editing: false, _editDraft: null })
              } else {
                s.root.update({ _editing: true, _editDraft: { ...s.dataDetail?.item } })
              }
            }
          },
          SaveBtn: {
            tag: 'button',
            type: 'button',
            hide: (el, s) => !s.root?._editing,
            text: (el, s) => s.root?._saving ? 'Saving…' : 'Save',
            disabled: (el, s) => !!s.root?._saving,
            padding: 'Z A',
            round: 'Z',
            borderWidth: '0',
            background: 'blue',
            color: 'white',
            fontWeight: '600',
            cursor: 'pointer',
            margin: '0 0 0 Z',
            onClick: async (e, el, s) => {
              const detail = s.dataDetail
              const draft = s.root?._editDraft
              if (!detail?.item || !draft) return
              const id = detail.item.id || detail.item._id
              const kind = detail.kind
              // Collect only changed editable fields
              const changes = {}
              for (const [key, def] of Object.entries(_editableFields)) {
                if (!_isEditable(key, kind)) continue
                if (draft[key] !== undefined && draft[key] !== detail.item[key]) {
                  changes[key] = draft[key]
                }
              }
              if (!Object.keys(changes).length) {
                s.root.update({ _editing: false, _editDraft: null })
                return
              }
              s.root.update({ _saving: true })
              try {
                const sdk = await _getSDK()
                if (kind === 'project') await sdk.updateProject(id, changes)
                else if (kind === 'user') await sdk.updateUser(id, changes)
                const updated = { ...detail.item, ...changes }
                s.root.update({
                  dataDetail: { ...detail, item: updated },
                  _editing: false, _editDraft: null, _saving: false
                })
                _invalidate(kind === 'project' ? 'projects' : 'users')
              } catch (err) {
                console.error('[data] save failed', err)
                s.root.update({ _saving: false })
              }
            }
          }
        },

        SummaryInner: {
          flow: 'y',
          childrenAs: 'state',
          children: (el, s) => {
            const kind = s.dataDetail?.kind
            const it = s.root?._editing ? (s.root?._editDraft || {}) : (s.dataDetail?.item || {})
            const fields =
              kind === 'user'
                ? _userFields
                : kind === 'plan'
                  ? _planFields
                  : _projectFields
            return fields
              .filter((f) => it[f] != null && it[f] !== '' || (s.root?._editing && _isEditable(f, kind)))
              .map((f) => ({
                key: f, value: it[f] ?? '', _kind: kind,
                _editable: _isEditable(f, kind) && _isDataAdmin(),
                _editing: !!s.root?._editing
              }))
          },
          childExtends: {
            display: 'grid',
            gridTemplateColumns: '120px 1fr',
            gap: 'Z',
            padding: 'Z A',
            align: 'center',
            borderWidth: '0 0 1px 0',
            borderColor: 'line',
            borderStyle: 'solid',
            Label: {
              tag: 'span',
              color: 'caption',
              fontSize: 'Z2',
              fontWeight: '600',
              text: (el, s) => s.key
            },
            // Read-only value
            Value: {
              tag: 'span',
              hide: (el, s) => s._editing && s._editable,
              color: 'title',
              fontSize: 'Z2',
              textAlign: 'right',
              style: { wordBreak: 'break-word' },
              text: (el, s) => _fmt(s.value, s.key)
            },
            // Editable select
            Select: {
              tag: 'select',
              hide: (el, s) => !(s._editing && s._editable && _editableFields[s.key]?.type === 'select'),
              padding: 'V Z',
              round: 'Z',
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: 'blue',
              background: 'document',
              color: 'title',
              fontSize: 'Z2',
              cursor: 'pointer',
              value: (el, s) => s.value ?? '',
              childrenAs: 'state',
              children: (el, s) => (_editableFields[s.key]?.options || []).map(o => ({ value: o, text: o })),
              childExtends: { tag: 'option', value: (el, s) => s.value, text: (el, s) => s.text },
              onChange: (e, el, s) => {
                const draft = { ...(s.root?._editDraft || {}) }
                draft[s.key] = e.target.value
                s.root.update({ _editDraft: draft })
              }
            },
            // Editable input
            Input: {
              tag: 'input',
              type: 'text',
              hide: (el, s) => !(s._editing && s._editable && _editableFields[s.key]?.type === 'input'),
              padding: 'V Z',
              round: 'Z',
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: 'blue',
              background: 'document',
              color: 'title',
              fontSize: 'Z2',
              width: '100%',
              value: (el, s) => s.value ?? '',
              onInput: (e, el, s) => {
                const draft = { ...(s.root?._editDraft || {}) }
                draft[s.key] = e.target.value
                s.root.update({ _editDraft: draft })
              }
            }
          }
        }
      },

      // Plan features — pretty nested rendering of item.features.
      // Reuses the DataPlanFeature component so strings, {label,included,detail}
      // and nested {group, items} shapes all render the same as on cards.
      PlanFeatures: {
        hide: (el, s) => {
          if (s.dataDetail?.kind !== 'plan') return true
          const it = s.dataDetail?.item || {}
          return _normalizePlanFeatures(it.features || it.featureList).length === 0
        },
        flow: 'y',
        gap: 'A',
        padding: 'A C',
        SectionLabel: {
          tag: 'span',
          fontSize: 'Y2',
          fontWeight: '700',
          letterSpacing: '.12em',
          textTransform: 'uppercase',
          color: 'caption',
          text: (el, s) => {
            const it = s.dataDetail?.item || {}
            const n = _normalizePlanFeatures(it.features || it.featureList).length
            return `Features (${n})`
          }
        },
        FeaturesBox: {
          flow: 'y',
          gap: 'A',
          padding: 'A',
          round: 'Z',
          background: 'overlay',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'line',
          childrenAs: 'state',
          children: (el, s) => {
            const it = s.dataDetail?.item || {}
            return _normalizePlanFeatures(it.features || it.featureList)
          },
          childExtends: 'DataPlanFeature'
        }
      },

      // Pricing options — the /plans endpoint returns an array with every
      // available price tier (monthly/yearly/etc.) + any lookup keys.
      PlanPricingOptions: {
        hide: (el, s) => {
          if (s.dataDetail?.kind !== 'plan') return true
          return !(s.dataDetail?.item?.pricingOptions || []).length
        },
        flow: 'y',
        gap: 'Z',
        padding: 'A C',
        SectionLabel: {
          tag: 'span',
          fontSize: 'Y2',
          fontWeight: '700',
          letterSpacing: '.12em',
          textTransform: 'uppercase',
          color: 'caption',
          text: (el, s) =>
            `Pricing options (${(s.dataDetail?.item?.pricingOptions || []).length})`
        },
        OptionsList: {
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: 'Z',
          childrenAs: 'state',
          children: (el, s) => s.dataDetail?.item?.pricingOptions || [],
          childExtends: {
            flow: 'y',
            gap: 'V',
            padding: 'A',
            round: 'Z',
            background: 'overlay',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: (el, s) => (s.isPopular ? 'blue' : 'line'),
            Header: {
              flow: 'x',
              align: 'center space-between',
              Name: {
                tag: 'strong',
                color: 'title',
                text: (el, s) => s.displayName || s.key || ''
              },
              Popular: {
                hide: (el, s) => !s.isPopular,
                tag: 'span',
                padding: 'V Z',
                round: 'Z',
                background: 'blue',
                color: 'white',
                fontSize: 'Y2',
                fontWeight: '700',
                textTransform: 'uppercase',
                text: 'Popular'
              }
            },
            Price: {
              flow: 'x',
              align: 'baseline',
              gap: 'V',
              Amount: {
                tag: 'strong',
                color: 'title',
                fontSize: 'C',
                text: (el, s) => _formatPrice(s.amount) ?? '—'
              },
              Interval: {
                tag: 'span',
                color: 'caption',
                fontSize: 'Z2',
                text: (el, s) =>
                  s.interval && Number(s.amount) !== 0 ? `/ ${s.interval}` : ''
              }
            },
            Desc: {
              hide: (el, s) => !s.description,
              tag: 'span',
              color: 'caption',
              fontSize: 'Z2',
              text: (el, s) => s.description || ''
            },
            LookupKey: {
              hide: (el, s) => !s.lookupKey,
              tag: 'code',
              fontSize: 'Y2',
              color: 'caption',
              text: (el, s) => s.lookupKey || ''
            }
          }
        }
      },

      // Plan limits — when the plan exposes a numeric limits/quotas object,
      // render it as a neat two-column grid instead of raw JSON.
      PlanLimits: {
        hide: (el, s) => {
          if (s.dataDetail?.kind !== 'plan') return true
          const lim = s.dataDetail?.item?.limits || s.dataDetail?.item?.quotas
          return (
            !lim || typeof lim !== 'object' || Object.keys(lim).length === 0
          )
        },
        flow: 'y',
        gap: 'Z',
        padding: 'A C',
        SectionLabel: {
          tag: 'span',
          fontSize: 'Y2',
          fontWeight: '700',
          letterSpacing: '.12em',
          textTransform: 'uppercase',
          color: 'caption',
          text: 'Limits'
        },
        LimitsGrid: {
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: 'Z',
          childrenAs: 'state',
          children: (el, s) => {
            const lim =
              s.dataDetail?.item?.limits || s.dataDetail?.item?.quotas || {}
            return Object.entries(lim).map(([key, value]) => ({ key, value }))
          },
          childExtends: {
            flow: 'y',
            gap: 'V',
            padding: 'Z A',
            round: 'Z',
            background: 'overlay',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'line',
            Label: {
              tag: 'span',
              fontSize: 'Y2',
              color: 'caption',
              fontWeight: '600',
              letterSpacing: '.04em',
              textTransform: 'uppercase',
              text: (el, s) => s.key
            },
            Value: {
              tag: 'strong',
              color: 'title',
              fontSize: 'Z1',
              text: (el, s) => {
                const v = s.value
                if (v === true) return 'Yes'
                if (v === false) return 'No'
                if (v === null || v === undefined) return '—'
                if (typeof v === 'number' && !Number.isFinite(v)) return '∞'
                if (typeof v === 'object') return JSON.stringify(v)
                return String(v)
              }
            }
          }
        }
      },

      // Local profile — shown only when the selected user's email matches
      // a row in root.people. Clicking opens the intranet profile modal
      // (same mechanism as AppHeader's ProfileItem: root.profileEmail = …).
      LocalUserCard: {
        hide: (el, s) =>
          s.dataDetail?.kind !== 'user' ||
          !_localMatch(s.root, s.dataDetail?.item?.email),
        flow: 'y',
        gap: 'Z',
        padding: 'A C',
        SectionLabel: {
          tag: 'span',
          fontSize: 'Y2',
          fontWeight: '700',
          letterSpacing: '.12em',
          textTransform: 'uppercase',
          color: 'caption',
          text: 'Local profile'
        },
        Card: {
          flow: 'x',
          align: 'center',
          gap: 'A',
          padding: 'A',
          round: 'Z',
          background: 'overlay',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'line',
          cursor: 'pointer',
          ':hover': { background: 'overlayStrong', borderColor: 'blue' },
          onClick: (e, el, s) => {
            const m = _localMatch(s.root, s.dataDetail?.item?.email)
            if (!m) return
            s.root.update({
              dataDetail: null,
              profileEmail: m.email
            })
          },

          Avatar: {
            extends: 'Avatar',
            avatarType: 'initials',
            boxSize: 'C C',
            src: (el, s) =>
              el.call(
                'avatarUrlFor',
                _localMatch(s.root, s.dataDetail?.item?.email)?.email
              )
          },

          Info: {
            flow: 'y',
            gap: 'V',
            flex: '1',
            minWidth: '0',
            Name: {
              tag: 'span',
              color: 'title',
              fontSize: 'Z1',
              fontWeight: '600',
              style: {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              },
              text: (el, s) =>
                _localMatch(s.root, s.dataDetail?.item?.email)?.name || ''
            },
            Meta: {
              tag: 'span',
              color: 'caption',
              fontSize: 'Z2',
              text: (el, s) => {
                const m = _localMatch(s.root, s.dataDetail?.item?.email)
                if (!m) return ''
                const email = String(m.email || '').toLowerCase()
                const team = (s.root?.teamsByEmail?.[email] || [])[0]?.name
                return [m.position, team, m.location].filter(Boolean).join(' · ')
              }
            },
            Email: {
              tag: 'span',
              color: 'caption',
              fontSize: 'Z2',
              text: (el, s) =>
                _localMatch(s.root, s.dataDetail?.item?.email)?.email || ''
            }
          },

          OpenHint: {
            tag: 'span',
            color: 'blue',
            fontSize: 'Z2',
            fontWeight: '600',
            text: 'Open profile →'
          }
        }
      },

      // Members — only for projects. Rows from item.members with
      // name / email / role. Hidden when the list is empty.
      Members: {
        hide: (el, s) => {
          const d = s.dataDetail
          if (!d || (d.kind !== 'project' && d.kind !== 'organization')) return true
          const m = d.item?.members
          return !Array.isArray(m) || m.length === 0
        },
        flow: 'y',
        gap: 'Z',
        padding: 'A C',
        SectionLabel: {
          tag: 'span',
          fontSize: 'Y2',
          fontWeight: '700',
          letterSpacing: '.12em',
          textTransform: 'uppercase',
          color: 'caption',
          text: (el, s) =>
            `Members (${(s.dataDetail?.item?.members || []).length})`
        },
        MembersList: {
          flow: 'y',
          round: 'Z',
          background: 'overlay',
          borderWidth: '1px',
          borderColor: 'line',
          borderStyle: 'solid',
          childrenAs: 'state',
          children: (el, s) => s.dataDetail?.item?.members || [],
          childExtends: {
            flow: 'x',
            align: 'center',
            gap: 'A',
            padding: 'Z A',
            borderWidth: '0 0 1px 0',
            borderColor: 'line',
            borderStyle: 'solid',
            Name: {
              tag: 'span',
              color: 'title',
              fontWeight: '500',
              flex: '1',
              minWidth: '0',
              style: {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              },
              text: (el, s) =>
                (s.user && (s.user.name || s.user.email)) ||
                s.name ||
                s.email ||
                s.userId ||
                s.user_id ||
                ''
            },
            Email: {
              tag: 'span',
              color: 'caption',
              fontSize: 'Z2',
              text: (el, s) => (s.user && s.user.email) || s.email || ''
            },
            Role: {
              tag: 'span',
              color: 'title',
              fontSize: 'Z2',
              fontWeight: '600',
              width: 'D',
              textAlign: 'right',
              text: (el, s) =>
                s.role || (s.permissions && String(s.permissions)) || ''
            }
          }
        }
      },

      // Domains — only for projects. `domains` is either an array or an
      // object keyed by slot (main, www, …) with `.domain` strings.
      Domains: {
        hide: (el, s) => _domainsList(s.dataDetail).length === 0,
        flow: 'y',
        gap: 'Z',
        padding: 'A C',
        SectionLabel: {
          tag: 'span',
          fontSize: 'Y2',
          fontWeight: '700',
          letterSpacing: '.12em',
          textTransform: 'uppercase',
          color: 'caption',
          text: (el, s) => `Domains (${_domainsList(s.dataDetail).length})`
        },
        DomainsList: {
          flow: 'x',
          gap: 'Z',
          style: { flexWrap: 'wrap' },
          childrenAs: 'state',
          children: (el, s) => _domainsList(s.dataDetail),
          childExtends: {
            tag: 'a',
            padding: 'V Z1',
            round: 'Z',
            background: 'overlay',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'line',
            color: 'title',
            fontSize: 'Z2',
            textDecoration: 'none',
            target: '_blank',
            href: (el, s) => {
              const host = s.domain || ''
              if (!host) return '#'
              return /^https?:\/\//.test(host) ? host : `https://${host}`
            },
            text: (el, s) => {
              const label = s.domain || s.slot || ''
              return s.slot && s.slot !== s.domain
                ? `${s.slot}: ${s.domain}`
                : label
            }
          }
        }
      },

      // Permissions — shows item.permissions (object or array).
      // Applies to both projects (project-level ACLs) and users (globalRole etc).
      Permissions: {
        hide: (el, s) => {
          const it = s.dataDetail?.item || {}
          const p = it.permissions
          if (p == null) return true
          if (Array.isArray(p)) return p.length === 0
          if (typeof p === 'object') return Object.keys(p).length === 0
          return false
        },
        flow: 'y',
        gap: 'Z',
        padding: 'A C C',
        SectionLabel: {
          tag: 'span',
          fontSize: 'Y2',
          fontWeight: '700',
          letterSpacing: '.12em',
          textTransform: 'uppercase',
          color: 'caption',
          text: 'Permissions'
        },
        PermissionsList: {
          flow: 'y',
          round: 'Z',
          background: 'overlay',
          borderWidth: '1px',
          borderColor: 'line',
          borderStyle: 'solid',
          childrenAs: 'state',
          children: (el, s) => {
            const p = s.dataDetail?.item?.permissions
            if (!p) return []
            if (Array.isArray(p))
              return p.map((v, i) => ({ key: String(i), value: v }))
            if (typeof p === 'object')
              return Object.entries(p).map(([k, v]) => ({ key: k, value: v }))
            return [{ key: 'value', value: p }]
          },
          childExtends: {
            flow: 'x',
            align: 'start space-between',
            gap: 'B',
            padding: 'Z A',
            borderWidth: '0 0 1px 0',
            borderColor: 'line',
            borderStyle: 'solid',
            Label: {
              tag: 'span',
              color: 'caption',
              fontSize: 'Z2',
              fontWeight: '600',
              text: (el, s) => s.key
            },
            Value: {
              tag: 'span',
              color: 'title',
              fontSize: 'Z2',
              textAlign: 'right',
              style: { wordBreak: 'break-word', maxWidth: '60%' },
              text: (el, s) => _fmt(s.value)
            }
          }
        }
      },

      // Footer — only for projects. Three deep-link buttons.
      ModalFooter: {
        hide: (el, s) => s.dataDetail?.kind !== 'project',
        flow: 'x',
        align: 'center',
        gap: 'Z',
        padding: 'A C B',
        borderWidth: '1px 0 0 0',
        borderStyle: 'solid',
        borderColor: 'line',
        style: { flexWrap: 'wrap' },

        DirectBtn: {
          tag: 'a',
          target: '_blank',
          hide: (el, s) => !_directUrl(s.dataDetail?.item),
          href: (el, s) => _directUrl(s.dataDetail?.item) || '#',
          padding: 'Z A',
          round: 'Z',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'line',
          background: 'overlay',
          color: 'title',
          fontSize: 'Z2',
          fontWeight: '500',
          textDecoration: 'none',
          ':hover': { background: 'overlayStrong' },
          text: 'Open direct'
        },
        PreviewBtn: {
          tag: 'a',
          target: '_blank',
          hide: (el, s) => !_previewUrl(s.dataDetail?.item),
          href: (el, s) => _previewUrl(s.dataDetail?.item) || '#',
          padding: 'Z A',
          round: 'Z',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'line',
          background: 'overlay',
          color: 'title',
          fontSize: 'Z2',
          fontWeight: '500',
          textDecoration: 'none',
          ':hover': { background: 'overlayStrong' },
          text: 'Open preview'
        },
        CanvasBtn: {
          tag: 'a',
          target: '_blank',
          hide: (el, s) => !_canvasUrl(s.dataDetail?.item),
          href: (el, s) => _canvasUrl(s.dataDetail?.item) || '#',
          padding: 'Z A',
          round: 'Z',
          borderWidth: '0',
          background: 'blue',
          color: 'white',
          fontSize: 'Z2',
          fontWeight: '600',
          textDecoration: 'none',
          ':hover': { filter: 'brightness(1.1)' },
          text: 'Open canvas'
        },

        // Transfer ownership — select from available orgs
        TransferGroup: {
          hide: (el, s) => !_isDataAdmin(),
          flow: 'x',
          align: 'center',
          gap: 'Z',
          margin: '0 0 0 auto',
          OrgSelect: {
            tag: 'select',
            padding: 'Z A',
            round: 'Z',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'line',
            background: 'document',
            color: 'title',
            fontSize: 'Z2',
            cursor: 'pointer',
            childrenAs: 'state',
            children: (el, s) => {
              const orgs = s.organizations || []
              const current = s.dataDetail?.item?.owner?.slug || s.dataDetail?.item?.owner?.username || ''
              return [
                { value: '', text: `Owner: ${current || '—'}` },
                ...orgs.map(o => ({ value: o.id || o._id, text: o.name, slug: o.slug }))
              ]
            },
            childExtends: { tag: 'option', value: (el, s) => s.value, text: (el, s) => s.text },
            onChange: async (e, el, s) => {
              const orgId = e.target.value
              if (!orgId) return
              const projectId = s.dataDetail?.item?.id || s.dataDetail?.item?._id
              if (!projectId) return
              try {
                const sdk = await _getSDK()
                await sdk.transferProjectOwnership(projectId, {
                  targetType: 'organization',
                  organizationId: orgId
                })
                // Refresh the detail
                const orgs = s.organizations || []
                const org = orgs.find(o => (o.id || o._id) === orgId)
                const detail = s.dataDetail
                if (detail?.item) {
                  detail.item.ownerType = 'organization'
                  detail.item.owner = { name: org?.name, slug: org?.slug, type: 'organization' }
                  detail.item.ownerOrganization = { name: org?.name, slug: org?.slug }
                  // Forward-compat: write workspace path so Phase-3-aware
                  // renderers (see _isOrgOwned / _ownerOrg) pick up the
                  // transfer without refetching.
                  detail.item.workspace = {
                    ...(detail.item.workspace || {}),
                    type: 'shared',
                    organization: { name: org?.name, slug: org?.slug }
                  }
                  s.root.update({ dataDetail: { ...detail } })
                }
                _invalidate('projects')
                e.target.value = ''
              } catch (err) {
                console.error('[data] transfer failed', err)
              }
            }
          }
        }
      }
    }
  }
}

// Renders one feature entry inside a plan card. Handles three input shapes:
//   "Unlimited projects"
//   { label, included: boolean, detail? }
//   { group: 'Collaboration', items: [...] }
export const DataPlanFeature = {
  flow: 'y',
  gap: 'V',

  // Group header — only shown when the feature object looks like a nested group.
  GroupHeader: {
    hide: (el, s) => !(s?.group && Array.isArray(s?.items)),
    tag: 'span',
    fontSize: 'Y2',
    fontWeight: '700',
    letterSpacing: '.12em',
    textTransform: 'uppercase',
    color: 'caption',
    text: (el, s) => s.group || ''
  },

  // Nested items — only when this is a group. Each item follows the same
  // shape rules as flat features (string or { label, included }).
  GroupItems: {
    hide: (el, s) => !Array.isArray(s?.items),
    flow: 'y',
    gap: 'V',
    padding: '0 0 0 A',
    childrenAs: 'state',
    children: (el, s) => s.items || [],
    childExtends: 'DataPlanFeatureRow'
  },

  DataPlanFeatureRow: {
    hide: (el, s) => Array.isArray(s?.items)
  }
}

export const DataPlanFeatureRow = {
  flow: 'x',
  align: 'center left',
  gap: 'Z',
  fontSize: 'Z2',

  Mark: {
    tag: 'span',
    width: 'Z1',
    textAlign: 'center',
    fontWeight: '700',
    color: (el, s) => {
      if (typeof s === 'string') return 'green'
      return s?.included === false ? 'caption' : 'green'
    },
    text: (el, s) => {
      if (typeof s === 'string') return '✓'
      return s?.included === false ? '—' : '✓'
    }
  },

  Label: {
    tag: 'span',
    flex: '1',
    color: (el, s) =>
      typeof s !== 'string' && s?.included === false ? 'caption' : 'title',
    text: (el, s) => {
      if (typeof s === 'string') return s
      return s?.label || s?.name || s?.text || ''
    }
  },

  Detail: {
    hide: (el, s) => typeof s === 'string' || !(s?.detail || s?.value),
    tag: 'span',
    color: 'caption',
    fontSize: 'Y2',
    text: (el, s) => s?.detail || s?.value || ''
  }
}

export const DataProjectRow = {
  display: 'grid',
  gridTemplateColumns: '1fr 160px 100px 80px',
  gap: 'A',
  padding: 'Z1 A',
  align: 'center',
  cursor: 'pointer',
  borderWidth: '0 0 1px 0',
  borderColor: 'line',
  borderStyle: 'solid',
  ':hover': { background: 'overlayStrong' },
  onClick: (e, el, s) => _openDetail(s.root, 'project', s),
  NameGroup: {
    flow: 'x',
    align: 'center',
    gap: 'Z',
    minWidth: '0',
    Name: {
      tag: 'span',
      color: 'title',
      fontWeight: '500',
      style: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      },
      text: (el, s) => s.name || s.key || s.id || ''
    },
    Key: {
      tag: 'span',
      color: 'caption',
      fontSize: 'Y2',
      style: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      },
      text: (el, s) => {
        const owner = _ownerUsername(s)
        const slug = _projectSlug(s)
        return owner ? `${owner}/${slug}` : s.key || ''
      }
    }
  },
  OwnerCol: {
    flow: 'x',
    align: 'center left',
    gap: 'Y',
    minWidth: '0',
    Icon: {
      tag: 'span',
      fontSize: 'Y2',
      text: (el, s) => _isOrgOwned(s) ? '🏢' : '👤'
    },
    Text: {
      tag: 'span',
      color: 'subtitle',
      fontSize: 'Z2',
      style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
      text: (el, s) => {
        if (_isOrgOwned(s)) {
          const org = _ownerOrg(s)
          return org?.name || org?.slug || ''
        }
        return _ownerUsername(s) || ''
      }
    }
  },
  AccessCol: {
    textAlign: 'center',
    Badge: {
      tag: 'span',
      padding: 'V Z',
      round: 'Z',
      fontSize: 'Y2',
      fontWeight: '600',
      letterSpacing: '.06em',
      textTransform: 'uppercase',
      background: (el, s) => s.access === 'public' ? 'green.2' : 'overlayStrong',
      color: (el, s) => s.access === 'public' ? 'green' : 'caption',
      text: (el, s) => s.access || ''
    }
  },
  Members: {
    tag: 'span',
    color: 'caption',
    fontSize: 'Z2',
    textAlign: 'right',
    text: (el, s) =>
      s.memberCount != null
        ? `${s.memberCount} user${s.memberCount === 1 ? '' : 's'}`
        : ''
  }
}

export const DataSubscriptionRow = {
  display: 'grid',
  gridTemplateColumns: '1fr 200px 80px 100px',
  gap: 'A',
  padding: 'Z1 A',
  align: 'center',
  cursor: 'pointer',
  borderWidth: '0 0 1px 0',
  borderColor: 'line',
  borderStyle: 'solid',
  ':hover': { background: 'overlayStrong' },
  onClick: (e, el, s) => _openDetail(s.root, 'subscription', s),
  WorkspaceGroup: {
    flow: 'y',
    minWidth: '0',
    Name: {
      tag: 'span',
      color: 'title',
      fontWeight: '500',
      style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
      text: (el, s) => s.workspaceName || s.workspaceSlug || s.workspaceId || ''
    },
    Slug: {
      tag: 'span',
      color: 'caption',
      fontSize: 'Y2',
      text: (el, s) => s.workspaceSlug || ''
    }
  },
  Org: {
    tag: 'span',
    color: 'caption',
    fontSize: 'Z2',
    style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    text: (el, s) => s.orgName || ''
  },
  Tier: {
    ..._TIER_BADGE,
    textAlign: 'center',
    text: (el, s) => s.tier || '—'
  },
  Status: {
    tag: 'span',
    color: (el, s) => s.status === 'active' ? 'green' : 'caption',
    fontSize: 'Z2',
    textAlign: 'right',
    text: (el, s) => s.status || ''
  }
}

export const DataUserRow = {
  display: 'grid',
  gridTemplateColumns: '1fr 220px 100px 80px 80px',
  gap: 'A',
  padding: 'Z1 A',
  align: 'center',
  cursor: 'pointer',
  borderWidth: '0 0 1px 0',
  borderColor: 'line',
  borderStyle: 'solid',
  ':hover': { background: 'overlayStrong' },
  onClick: (e, el, s) => _openDetail(s.root, 'user', s),

  NameGroup: {
    flow: 'x',
    align: 'center',
    gap: 'Z',
    flex: '1',
    minWidth: '0',
    Name: {
      tag: 'span',
      color: 'title',
      fontWeight: '500',
      style: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      },
      text: (el, s) => s.name || s.username || s.email || s.id || ''
    },
    Username: {
      hide: (el, s) => !s.username,
      tag: 'span',
      color: 'caption',
      fontSize: 'Y2',
      text: (el, s) => `@${s.username}`
    },
    VerifiedBadge: {
      hide: (el, s) => !s.emailVerified,
      tag: 'span',
      title: 'Verified',
      padding: 'V Z',
      round: 'Z',
      background: 'green',
      color: 'white',
      fontSize: 'Y2',
      fontWeight: '700',
      letterSpacing: '.04em',
      textTransform: 'uppercase',
      text: '✓'
    }
  },

  Email: {
    tag: 'span',
    color: 'caption',
    fontSize: 'Z2',
    style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    text: (el, s) => s.email || ''
  },
  StatusCol: {
    textAlign: 'center',
    Badge: {
      tag: 'span',
      padding: 'V Z',
      round: 'Z',
      fontSize: 'Y2',
      fontWeight: '600',
      letterSpacing: '.06em',
      textTransform: 'uppercase',
      background: (el, s) => {
        const st = s.status || 'active'
        if (st === 'active') return 'green.2'
        if (st === 'pending') return 'yellow.2'
        return 'overlayStrong'
      },
      color: (el, s) => {
        const st = s.status || 'active'
        if (st === 'active') return 'green'
        if (st === 'pending') return 'yellow'
        return 'caption'
      },
      text: (el, s) => s.status || 'active'
    }
  },
  Provider: {
    tag: 'span',
    color: 'caption',
    fontSize: 'Y2',
    textAlign: 'center',
    text: (el, s) => s.authProvider || ''
  },
  Role: {
    tag: 'span',
    color: 'caption',
    fontSize: 'Z2',
    textAlign: 'right',
    text: (el, s) => s.globalRole || 'user'
  }
}

export const DataSidebarLink = {
  tag: 'button',
  type: 'button',
  Label: { tag: 'span', text: (el, s) => s.label || '' },
  flow: 'x',
  align: 'center left',
  width: '100%',
  textAlign: 'left',
  padding: 'Z A',
  round: 'Z',
  fontSize: 'A',
  borderWidth: '0',
  cursor: 'pointer',
  onClick: (e, el, s) => {
    e.preventDefault()
    s.root.update({ dataQuery: '' })
    let root = el
    while (root && !root.routes) root = root.parent
    if (root) el.router(s.href, root)
  },
  background: (el, s) => {
    const path = s.root?.currentPath || '/data/projects'
    return path === s.href || (s.key === 'projects' && path === '/data')
      ? 'overlay'
      : 'transparent'
  },
  color: (el, s) => {
    const path = s.root?.currentPath || '/data/projects'
    return path === s.href || (s.key === 'projects' && path === '/data')
      ? 'title'
      : 'subtitle'
  }
}
